import React, { useState, useMemo } from 'react';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { Switch } from '@fiverr-private/forms';
import { InfoCircleIcon } from '@fiverr-private/visuals';
import { Gql } from '@fiverr-private/dynamic_matching';
import { DrawerFooter } from '@fiverr-private/overlay';
import { appTranslate } from '../../i18n';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { briefCreationClickAction } from '../../monitoring/bqEvents';
import { Step } from '../../constants';

// Re-export types from dynamic_matching for consumers
export type ExtraWaveStatusInfo = Gql.ExtraWaveStatusInfo;
export const ExtraWaveStatus = Gql.ExtraWaveStatus;

interface BriefConfirmationScreenProps {
  extraWaveStatus?: ExtraWaveStatusInfo;
  onBackToEditing: () => void;
  onPostBrief: (shareWithMoreFreelancers: boolean) => void;
  isSubmitting?: boolean;
}

const calculateTimeRemaining = (epochTimeSeconds: number): { value: string; unit: string } => {
  const nowMs = Date.now();
  const epochTimeMs = epochTimeSeconds * 1000; // Convert to milliseconds
  const diffMs = epochTimeMs - nowMs;

  if (diffMs <= 0) {
    return { value: '0', unit: 'minutes' };
  }

  const hours = Math.round(diffMs / (1000 * 60 * 60));

  if (hours >= 1) {
    return { value: hours.toString(), unit: hours === 1 ? 'hour' : 'hours' };
  }

  const minutes = diffMs / (1000 * 60);
  return { value: Math.max(0, Math.round(minutes)).toString(), unit: Math.round(minutes) === 1 ? 'minute' : 'minutes' };
};
export const BriefConfirmationScreen: React.FC<BriefConfirmationScreenProps> = ({
  extraWaveStatus,
  onBackToEditing,
  onPostBrief,
  isSubmitting = false,
}) => {
  const { contextId, source, parentElement, variant } = useBriefCreationContext();
  const [shareWithMoreFreelancers, setShareWithMoreFreelancers] = useState(false);

  const toggleState = useMemo(() => {
    if (!extraWaveStatus) {
      return { visible: false, enabled: false, message: '' };
    }

    const { status, timeForNextWaveRequestEligibility } = extraWaveStatus;

    switch (status) {
      case Gql.ExtraWaveStatus.Active:
        return {
          visible: true,
          enabled: true,
          message: appTranslate('confirmation_screen.share_toggle_active'),
        };

      case Gql.ExtraWaveStatus.MaxLimitReached:
        return {
          visible: true,
          enabled: false,
          message: appTranslate('confirmation_screen.share_toggle_max_limit'),
        };

      case Gql.ExtraWaveStatus.Cooldown:
        const timeRemaining = timeForNextWaveRequestEligibility
          ? calculateTimeRemaining(timeForNextWaveRequestEligibility)
          : { value: '0', unit: 'minutes' };
        return {
          visible: true,
          enabled: false,
          message: appTranslate('confirmation_screen.share_toggle_cooldown', {
            params: { time: `${timeRemaining.value} ${timeRemaining.unit}` },
          }),
        };

      default:
        return { visible: false, enabled: false, message: '' };
    }
  }, [extraWaveStatus]);

  const handlePostBrief = () => {
    briefCreationClickAction({
      step: Step.Edit,
      action: 'update_brief',
      briefContextId: contextId,
      eventSource: source || '',
      parentElement,
      variant,
    });
    onPostBrief(shareWithMoreFreelancers && toggleState.enabled);
  };

  return (
    <Container width="100%" height="100%" display="flex" direction="column" justifyContent="spaceBetween">
      <Stack
        direction="column"
        gap="8"
        flex={1}
        justifyContent="center"
        alignItems="center"
        padding="8"
        backgroundColor="grey_200"
      >
        <Stack direction="column" gap="4" textAlign="center" maxWidth="600px">
          <Text size="b_xxl" fontWeight="bold">
            {appTranslate('confirmation_screen.title')}
          </Text>
          <Text size="b_md" color="bodySecondary">
            {appTranslate('confirmation_screen.description')}
          </Text>
        </Stack>

        {toggleState.visible && (
          <Stack
            direction="column"
            gap="4"
            width="100%"
            maxWidth="600px"
            cursor={toggleState.enabled ? 'pointer' : 'notAllowed'}
          >
            <Stack
              paddingY="5"
              paddingX="4"
              borderRadius="lg"
              borderWidth="sm"
              borderColor="grey_500"
              gap="3"
              alignItems="flexStart"
            >
              <Switch
                size="sm"
                checked={shareWithMoreFreelancers}
                onChange={(e) => setShareWithMoreFreelancers(e.target.checked)}
                disabled={!toggleState.enabled}
              />
              <Stack direction="column" gap="0.5" flex={1}>
                <Text fontWeight="bold" color={toggleState.enabled ? 'bodyPrimary' : 'bodySecondary'}>
                  {appTranslate('confirmation_screen.share_toggle_label')}
                </Text>
                <Text size="b_sm" color="bodySecondary">
                  {appTranslate('confirmation_screen.share_toggle_cta')}
                </Text>
              </Stack>
            </Stack>

            {!toggleState.enabled && toggleState.message && (
              <Stack gap="1.5" alignItems="center">
                <InfoCircleIcon color="grey_1200" />
                <Text size="b_sm" color="bodyPrimary">
                  {toggleState.message}
                </Text>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      <DrawerFooter>
        <Stack backgroundColor="white" direction="row" justifyContent="spaceBetween" alignItems="center" width="100%">
          <Button variant="ghost" onClick={onBackToEditing} disabled={isSubmitting}>
            {appTranslate('confirmation_screen.back_button')}
          </Button>
          <Button onClick={handlePostBrief} disabled={isSubmitting} loading={isSubmitting}>
            {appTranslate('confirmation_screen.post_button')}
          </Button>
        </Stack>
      </DrawerFooter>
    </Container>
  );
};
